import { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { useForm } from "react-hook-form";
import {
  useLazyProfileQuery,
  useUpdateUserProfileMutation,
} from "../../../../../../redux/api/user/userAPI";
import UpdateUserProfileRequestDTO from "../../../../dtos/request-dtos/update-user-profile-request-dto";
import AppButton from "../../../../ui-elements/buttons/app-button/app-button";
import AppInput from "../../../../ui-elements/input/app-input";
import MaterialIcon from "../../../../ui-elements/material-icon/material-icon";
import { noSpecialCharsNoWhitespaceNoNumbersStartEndRegex } from "../../../../utils/regex";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/toast";

interface IProps {
  firstName: string;
  lastName: string;
  username: string;
  getExtendedProfile: () => void;
  showEditIcon: boolean;
}

const defaultFormValue = { firstName: "", lastName: "", NIC: "", username: "" };

const UserProfileData = (props: IProps) => {
  const { firstName, lastName, username, getExtendedProfile, showEditIcon } =
    props;
  const [isEditMode, setIsEditMode] = useState(false);
  const [updateUserProfile, { isLoading: isLoadingUpdateUserProfile }] =
    useUpdateUserProfileMutation();
  const [getProfile, { isFetching: isFetchingProfile }] = useLazyProfileQuery();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { ...defaultFormValue },
  });

  useEffect(() => {
    reset({ ...defaultFormValue });
    if (isEditMode) {
      setValue("firstName", firstName);
      setValue("lastName", lastName);
      setValue("username", username);
    }
  }, [isEditMode, firstName, lastName, username, setValue]);

  const onSubmit = (data: UpdateUserProfileRequestDTO) => {
    if (isEditMode) {
      updateUserProfile({
        ...data,
      })
        .unwrap()
        .then(() => {
          ReactGA.event({
            category: "Successful",
            action: "Edit Profile",
          });
          showSuccessMessage("User Profile Details Updated Successfully");
          return getProfile().unwrap();
        })
        .then(() => {
          getExtendedProfile();
        })
        .catch(() => {
          showErrorMessage("Editing User Profile Details Unsuccessful");
        });
    }
    setIsEditMode(false);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`bg-white rounded-4 d-flex align-items-start justify-content-between p-3 w-100`}
    >
      <div className="d-flex flex-column gap-3 w-100">
        {!isEditMode ? (
          <>
            <div className="d-flex flex-column">
              <span className="text-light font-size-12 font-weight-400">
                First Name
              </span>
              <span className="text-dark font-size-16 font-weight-500">
                {firstName || "-"}
              </span>
            </div>
            <div className="d-flex flex-column">
              <span className="text-light font-size-12 font-weight-400">
                Last Name
              </span>
              <span className="text-dark font-size-16 font-weight-500">
                {lastName || "-"}
              </span>
            </div>
            <div className="d-flex flex-column">
              <span className="text-light font-size-12 font-weight-400">
                Username
              </span>
              <span className="text-dark font-size-16 font-weight-500">
                {username || "-"}
              </span>
            </div>
          </>
        ) : (
          <div className="d-flex flex-column gap-2">
            <div>
              <span className="text-dark font-size-14">First Name</span>
              <AppInput
                name="firstName"
                placeholder="First Name"
                register={register("firstName", {
                  required: "First name is required",
                  minLength: {
                    value: 2,
                    message: "This field must contain at least 2 characters",
                  },
                  maxLength: {
                    value: 50,
                    message:
                      "You have exceeded the maximum number of 50 characters in this field",
                  },
                  pattern: {
                    value: noSpecialCharsNoWhitespaceNoNumbersStartEndRegex,
                    message:
                      "Entered value can't start/end or contain only white spaces and can't contain any special characters and numbers.",
                  },
                })}
                errors={errors}
              />
            </div>
            <div>
              <span className="text-dark font-size-14">Last Name</span>
              <AppInput
                name="lastName"
                placeholder="Last Name"
                register={register("lastName", {
                  required: "Last name is required",
                  minLength: {
                    value: 2,
                    message: "This field must contain at least 2 characters",
                  },
                  maxLength: {
                    value: 50,
                    message:
                      "You have exceeded the maximum number of 50 characters in this field",
                  },
                  pattern: {
                    value: noSpecialCharsNoWhitespaceNoNumbersStartEndRegex,
                    message:
                      "Entered value can't start/end or contain only white spaces and can't contain any special characters and numbers.",
                  },
                })}
                errors={errors}
              />
            </div>
            <div>
              <span className="text-dark font-size-14">User Name</span>
              <AppInput
                name="username"
                placeholder="User Name"
                register={register("username")}
                errors={errors}
                disabled={true}
              />
            </div>
            <div className="d-flex mt-2">
              <AppButton
                text="Save"
                size="small"
                onClick={handleSubmit(onSubmit)}
                isLoading={isLoadingUpdateUserProfile || isFetchingProfile}
                className="rounded-4"
              />
              <AppButton
                text="Cancel"
                variant="transparent"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  setIsEditMode(false);
                }}
              />
            </div>
          </div>
        )}
      </div>
      {!isEditMode && showEditIcon && (
        <div className="ms-3 flex-shrink-0 align-self-start">
          <MaterialIcon
            icon="border_color"
            color="#7483CC"
            className="cursor-pointer"
            size={20}
            onClick={() => setIsEditMode(true)}
          />
        </div>
      )}
    </form>
  );
};

export default UserProfileData;
